import * as React from 'react';
import { connect } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import '../Main.scss';

import {
    Col,
    Container,
    Row
} from 'reactstrap';

import { ApplicationState } from '../../Store';
import {
    GetPortalLanguage,
    GetPortalLoginText,
    RetrieveLoginText,
} from '../../Store/PortalAssets';
import {
    GetBasicSettings,
    GetTenantNameId,
    IsLenderDisabled,
    MultiTenantSettings,
    TenantActionCreators,
    TenantBasicSettings,
    TenantLoginDestination,
    TenantLoginText,
} from '../../Store/Tenant';
import { GetRedirectAfterLogin, UIRedirectInfo } from '../../Store/UIState';

import LoginButton from './LoginButton';
import { MultiTenantLoginListContainer } from './MultiTenantLoginListContainer';

import { pathConstants } from '../../Utils/Constants';
import { ExtractUrlParameters } from '../../Utils/PathUtils';

import { LogException, LogMessage, SeverityLevel } from '../../Utils/Logging';

import { PortalTextLogin } from '../../Models/Api/strongbox.financialportal';

import { AppTitleHeader } from '../Utils/AppTitleHeader';

import { GenerateCommonLoginContent } from './CommonContent';

type InjectedReduxState = {
    tenantNameId: string;
    redirect?: UIRedirectInfo;
    tenantBasicSettings?: TenantBasicSettings;
    isTenantDisabled: boolean;
    portalLoginText?: PortalTextLogin;
    portalLanguage?: string;
};

type InjectedActionCreators = typeof TenantActionCreators;

type LoginProps = {
    defaultWelcomeText: TenantLoginText[];
};

type Props = LoginProps & InjectedReduxState & InjectedActionCreators;

const LoginComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        portalLoginText,
        portalLanguage,
        defaultWelcomeText,
    } = props;

    const location = useLocation();

    const [inactivityTimeout, setInactivityTimeout] = React.useState<boolean>(false);
    const [loginThisPortalName, setLoginThisPortalName] = React.useState<string>('');
    const [loginContainer, setLoginContainer] = React.useState<React.ReactElement | undefined>(undefined);
    const [welcomeText, setWelcomeText] = React.useState<TenantLoginText[]>(defaultWelcomeText);
    const [showLoginButton, setShowLoginButton] = React.useState<boolean>(true);
    // navToLogin will be set when we arrive at the login page with the 
    // pathConstants.autoNavToLogin ('autologin') present in the URL and
    // set to true.  In that case we'll just render the <LoginButton and it
    // in turn will just execute the Auth0 login sequence for this portal.
    // That happens in a multi-tenant scenario where they opt to go straight
    // from one portal to a different portal and immediately login.
    const [navToLogin, setNavToLogin] = React.useState<boolean>(false);
    const [separatorAfterWelcome, setSeparatorAfterWelcome] = React.useState<boolean>(false);
    const [placeButtonAfterLine, setPlaceButtonAfterLine] = React.useState<number>(1);

    const resetDefaultLoginSettings = (): void => {
        setLoginThisPortalName('');
        setLoginContainer(undefined);
        setShowLoginButton(true);
        setPlaceButtonAfterLine(1);
        setSeparatorAfterWelcome(false);
        setNavToLogin(false);
        setWelcomeText(defaultWelcomeText);
    }

    React.useEffect(() => {
        let inactivity = false;

        RetrieveLoginText();
    
        ExtractUrlParameters(
            location.search,
            (key: string, value: string): void => {
                switch (key.toLowerCase()) {
                    case pathConstants.queryParamInactivityTimeout:
                        if (value.toLowerCase() === 'true') {
                            inactivity = true;
                        }
                        break;
                    case pathConstants.autoNavToLogin: {
                        const setting = value.toLowerCase() === 'true';
                        setNavToLogin(setting);
                        break;
                    }
                }
            }
        );

        setInactivityTimeout(inactivity);
        // I want this to execute equivalent to componentDidMount so this is appropriate
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const executeLogin = (url: string, autoProcessLogin: boolean): void => {
        window.location.href = `${url}?redirected=true&${pathConstants.autoNavToLogin}=${autoProcessLogin}`
    }

    const setLoginDestinations = (loginDestinations: TenantLoginDestination[], includeThisPortalInButtons: boolean, autoProcessLogin: boolean): void => {
        const thisPortal = loginDestinations.find(dest => dest.tenantName === props.tenantNameId);
        if (!!thisPortal) {
            setLoginThisPortalName(thisPortal.name);
        }

        setLoginContainer((
            <MultiTenantLoginListContainer
                loginDestinations={loginDestinations}
                thisPortal={thisPortal}
                includeThisPortal={includeThisPortalInButtons}
                executeLogin={(url) => executeLogin(url, autoProcessLogin)}
            />
        ));
    }

    const setMultiTenant = (multiTenantSettings: MultiTenantSettings): void => {
        setLoginDestinations(
            multiTenantSettings.loginDestinations,
            multiTenantSettings.defaultLogin === false,
            multiTenantSettings.passLoginThrough === true
        );
        if (!!multiTenantSettings.welcomeText) {
            setWelcomeText(multiTenantSettings.welcomeText);
        } else {
            setWelcomeText(defaultWelcomeText);
        }
        setShowLoginButton(multiTenantSettings.defaultLogin !== false);

        setSeparatorAfterWelcome(
            multiTenantSettings.showSeparatorAfterWelcomeText !== undefined ?
                multiTenantSettings.showSeparatorAfterWelcomeText : false
        )

        setPlaceButtonAfterLine(
            multiTenantSettings.placeLoginAfterWelcomeLine !== undefined ?
                multiTenantSettings.placeLoginAfterWelcomeLine : 1
        );
    }

    React.useEffect(() => {
        if (!!props.tenantBasicSettings) {
            if (!!props.tenantBasicSettings.multiTenantSettings) {
                // We have to error out if both passLoginThrough and defaultLogin
                // are false.  Otherwise you have no default login button on the destination
                // page and you don't pass through to auto-login.  Basically you can never
                // login.  There are more complex ways to handle this but probably 
                // overkill for what we're trying to accomplish here on something that's
                // going to be configured by us and should be relatively obvious that it's 
                // failing if we put in an invalid configuration.
                if (!(props.tenantBasicSettings.multiTenantSettings.passLoginThrough ||
                    props.tenantBasicSettings.multiTenantSettings.defaultLogin)) {
                    const msg = 'Invalid configuration, passLoginThrough and defaultLogin cannot both be false';

                    LogMessage(msg, SeverityLevel.Error);
                    console.error(msg);

                    resetDefaultLoginSettings();
                } else {
                    try {
                        setMultiTenant(props.tenantBasicSettings.multiTenantSettings);
                    } catch (exception) {
                        const msg = 'Exception caught processing config.json file. Resetting to defaults';

                        LogException(
                            msg,
                            exception
                        );
                        console.error(msg);

                        resetDefaultLoginSettings();
                    }
                }
            } else {
                resetDefaultLoginSettings();
            }
        } else {
            resetDefaultLoginSettings();
        }
        // tenantBasicSettings never actually changes in one session so this does not need to be in the dependency list
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.tenantNameId, props.tenantBasicSettings]);

    const rowContent = React.useMemo(() => {
        return GenerateCommonLoginContent(
            welcomeText,
            loginThisPortalName,
            showLoginButton,
            placeButtonAfterLine,
            separatorAfterWelcome,
            portalLoginText,
            props.redirect,
            portalLanguage
        );

        // These are the actual dependencies I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [welcomeText, loginThisPortalName, portalLoginText]);

    if (props.isTenantDisabled) {
        return <Navigate to={pathConstants.tenantLenderDisabled} />
    }
    if (inactivityTimeout) {
        return <Navigate to={pathConstants.slidingWindowTimeout} />
    }

    return (
        <div
            className={`full-screen-region lender-region content-region control-region control-region-lender login-region`}
        >
            {navToLogin && (
                <LoginButton
                    redirectInfo={props.redirect}
                    autoNavToLogin={true}
                    portalLanguage={portalLanguage}
                />
            )}

            {!navToLogin && (
                <Container className={'login-content-container login-content-elements strongbox-basic-content-container'}>
                    <AppTitleHeader />
                    <Row>
                        <Col>
                            {rowContent.map((pg, pgIndex) => (
                                <Row 
                                    className={pg.rowClassName}
                                    key={`tenant-welcome-paragraph-row-${pgIndex}`}
                                >
                                    <Col className={'col-centered'}>
                                        {pg.content}
                                    </Col>
                                </Row>
                            ))}
                        </Col>
                    </Row>
                    {!!props.tenantBasicSettings.multiTenantSettings && !!loginContainer && (
                        <React.Fragment>
                            <div className={'login-team-selection-container'}>
                                <Row>
                                    <Col className={'col-centered'}>
                                        <h1>{portalLoginText?.selectYourTeam || ''}</h1>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className={'col-centered'}>
                                        {loginContainer}
                                    </Col>
                                </Row>
                            </div>
                        </React.Fragment>
                    )}
                </Container>
            )}
        </div>
    );
}

export const Login = connect<InjectedReduxState, InjectedActionCreators, LoginProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            tenantNameId: GetTenantNameId(appState),
            redirect: GetRedirectAfterLogin(appState),
            tenantBasicSettings: GetBasicSettings(appState),
            isTenantDisabled: IsLenderDisabled(appState),
            portalLoginText: GetPortalLoginText(appState),
            portalLanguage: GetPortalLanguage(appState),
        };

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...TenantActionCreators,
        },
        dispatch
    )
)(LoginComponent);
